import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import SockJS from "sockjs-client";

import {
  CHAT_APP_DESTINATION,
  CHAT_FEATURE_ENABLED,
  CHAT_SOCKET_URL,
  CHAT_TOPIC_PREFIX,
} from "./config";
import { chatTokenManager } from "./token";

const DEFAULT_SOCKET_URL = "/websocket/tracker";

type MessageHandler = (payload: any) => void;

export interface ChatSocketServiceInterface {
  subscribe(topic: string, handler: MessageHandler): Promise<() => void>;
  subscribeToConversation(conversationId: string, handler: MessageHandler): Promise<() => void>;
  subscribeToRoster(handler: MessageHandler): Promise<() => void>;
  send(destination: string, body: unknown): Promise<void>;
  sendToConversation(conversationId: string, body: unknown): Promise<void>;
  sendReadReceipt(conversationId: string, userId: string): Promise<void>;
}

class ActiveChatSocketService implements ChatSocketServiceInterface {
  private client: Client | null = null;
  private connectionPromise: Promise<Client> | null = null;
  private readonly subscriptions = new Map<string, StompSubscription>();

  private async ensureClient(): Promise<Client> {
    if (this.client && this.client.connected) {
      return this.client;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = this.establishConnection().finally(() => {
      this.connectionPromise = null;
    });

    return this.connectionPromise;
  }

  private async establishConnection(): Promise<Client> {
    const token = await chatTokenManager.getToken();

    const client = new Client({
      reconnectDelay: 5000,
      webSocketFactory: () => new SockJS(CHAT_SOCKET_URL || DEFAULT_SOCKET_URL),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (import.meta.env.DEV) {
      client.debug = (message) => {
        if (message.includes("PING")) return;
        console.debug("[chat-socket]", message);
      };
    }

    const connectedClient = await new Promise<Client>((resolve, reject) => {
      let settled = false;

      client.onConnect = () => {
        settled = true;
        this.client = client;
        resolve(client);
      };

      client.onStompError = (frame) => {
        if (!settled) {
          settled = true;
          reject(new Error(frame.headers?.message || "STOMP error"));
        }
      };

      client.onWebSocketError = (event) => {
        if (!settled) {
          settled = true;
          reject(event instanceof Error ? event : new Error("WebSocket connection failed"));
        }
      };

      client.activate();

      setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error("Timed out connecting to chat socket"));
        }
      }, 15000);
    });

    connectedClient.onDisconnect = () => {
      this.client = null;
      this.subscriptions.clear();
    };

    return connectedClient;
  }

  async subscribe(topic: string, handler: MessageHandler): Promise<() => void> {
    const client = await this.ensureClient();
    const subscription = client.subscribe(topic, (message: IMessage) => {
      if (!message.body) return;
      try {
        const payload = JSON.parse(message.body);
        handler(payload);
      } catch (error) {
        console.warn("Failed to parse chat message", error);
      }
    });

    this.subscriptions.set(topic, subscription);

    return () => {
      subscription.unsubscribe();
      this.subscriptions.delete(topic);
    };
  }

  async subscribeToConversation(conversationId: string, handler: MessageHandler): Promise<() => void> {
    const topic = `${CHAT_TOPIC_PREFIX}/${conversationId}`;
    return this.subscribe(topic, handler);
  }

  async subscribeToRoster(handler: MessageHandler): Promise<() => void> {
    return this.subscribe(CHAT_TOPIC_PREFIX, handler);
  }

  async send(destination: string, body: unknown) {
    const client = await this.ensureClient();
    client.publish({
      destination,
      body: JSON.stringify(body ?? {}),
    });
  }

  async sendToConversation(conversationId: string, body: unknown) {
    return this.send(`${CHAT_APP_DESTINATION}/${conversationId}`, body);
  }

  async sendReadReceipt(conversationId: string, userId: string) {
    return this.send(`${CHAT_APP_DESTINATION}/${conversationId}/read`, { userId });
  }
}

class DisabledChatSocketService implements ChatSocketServiceInterface {
  private createError() {
    return new Error("Telemedicine chat socket is disabled");
  }

  async subscribe(): Promise<() => void> {
    throw this.createError();
  }

  async subscribeToConversation(): Promise<() => void> {
    throw this.createError();
  }

  async subscribeToRoster(): Promise<() => void> {
    throw this.createError();
  }

  async send(): Promise<void> {
    throw this.createError();
  }

  async sendToConversation(): Promise<void> {
    throw this.createError();
  }

  async sendReadReceipt(): Promise<void> {
    throw this.createError();
  }
}

export const chatSocketService: ChatSocketServiceInterface = CHAT_FEATURE_ENABLED
  ? new ActiveChatSocketService()
  : new DisabledChatSocketService();

